import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'
import { decodeContentRef, decodeTopicRef } from '@tweddle-se-team/common/refs'
import { isMobile } from 'react-device-detect'
import { useDispatch, useSelector, useStore } from 'react-redux'
import qs from 'qs'
import usePublication from '@/pages/publicationContent/hooks/usePublication'
import useApi from '@/hooks/useApi'
import { productSelectors, productActions } from '@/store/products'
import { publicationActions } from '@/store/publication'
import { searchActions } from '@/store/search'
import BrandPublicationViewer from './publicationContent/strategies'
import { getInitialStoreSetup } from '@/store/helpers/storeHelpers'
import headerHelper from '@/store/helpers/headerHelper'
import PublicationNotFound from '@/components/shared/publicationNotFound'
import PreviewModeBanner from '@/components/shared/previewModeBanner'
import { reduxWrapper } from '@/store'
import { logger, withLogger } from '@/utils/logger'
import { generalConstants } from '@tweddle-se-team/common/constants'
import apiWrapper from '@/utils/apiHelpers/apiWrapper'
import ServiceWarrantyUnavailable from '@/components/serviceWarranty/unavailable'
import { resolveQueryParam } from '@/utils/queryParams'

function ContentPage() {
  const store = useStore()
  const router = useRouter()
  const dispatch = useDispatch()
  const apiHook = useApi()
  const publicationParams = useMemo(
    () =>
      router.query.publicationState === generalConstants.PUBLICATION_STATES.DRAFT
        ? { publicationState: router.query.publicationState }
        : {},
    [router.query.publicationState]
  )
  const { loadPublication, getRelatedPublicationsByLanguage } = usePublication({ params: publicationParams })
  const { brand } = useSelector(productSelectors.selectFilterBy)
  const [publication, setPublication] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [publicationContentOverride, setPublicationContentOverride] = useState(null)

  const {
    id: idFromQuery,
    topicId: topicIdFromQuery,
    publicationType: publicationTypeFromQuery,
    withoutNav,
    hideMobileToc,
    skipToc,
    serviceWarranty
  } = router.query
  const isServiceWarrantyMode = serviceWarranty === 'true'
  const withoutNavBool = withoutNav === 'true'
  const skipTocBool = skipToc === 'true'
  const showTocInitially = withoutNavBool ? false : !(isMobile && hideMobileToc !== undefined)

  const storeSetupRanRef = useRef(false)
  const pubRequestedRef = useRef(false)

  const initStore = useCallback(async () => {
    try {
      await getInitialStoreSetup({ store })
    } catch (e) {
      console.warn('[content] initStore failed (ok offline):', e)
    }
  }, [store])

  const contentId = useMemo(() => {
    if (!router.isReady) return
    // A ref arrives from the URL and becomes a value here — the read side of the boundary the
    // sitemap and every deep link write to (TB-11946). Identity today.
    return decodeContentRef(resolveQueryParam(idFromQuery, 'id'))
  }, [router.isReady, idFromQuery])

  const serviceWarrantyQueryString = useMemo(() => {
    if (!router.isReady || !isServiceWarrantyMode) return ''

    const resolveQuery = { ...router.query }
    delete resolveQuery.serviceWarranty
    delete resolveQuery.id
    delete resolveQuery.topicId

    return qs.stringify(resolveQuery)
  }, [isServiceWarrantyMode, router.isReady, router.query])

  const topicId = useMemo(() => {
    if (!router.isReady) return
    return decodeTopicRef(resolveQueryParam(topicIdFromQuery, 'topicId'))
  }, [router.isReady, topicIdFromQuery])

  const publicationType = useMemo(
    () => resolveQueryParam(publicationTypeFromQuery, 'publicationType'),
    [publicationTypeFromQuery]
  )

  const fetchPublication = useCallback(
    async id => {
      if (!router.isReady) return
      if (!contentId) return

      try {
        const requestParams = publicationType ? { publicationType } : {}
        const pub = await loadPublication(id, requestParams)
        const publicationBrand = pub?.brand?.toLowerCase()

        if (publicationBrand) {
          dispatch(productActions.SET_BRAND(publicationBrand))
          dispatch(searchActions.SET_BRAND(publicationBrand))
        }

        setPublication(pub)
        if (pub._id) {
          const relatedPublications = await getRelatedPublicationsByLanguage(id, publicationType)
          dispatch(publicationActions.SET_RELATED_PUBLICATIONS(relatedPublications))
        }
      } catch (err) {
        console.error('[content] loadPublication error:', err)
        setError('Failed to load publication')
      } finally {
        setLoading(false)
      }
    },
    [router.isReady, contentId, publicationType, loadPublication, getRelatedPublicationsByLanguage, dispatch]
  )

  const updateHeader = useCallback(async () => {
    if (!publication) return
    const { brand, model, modelType, year, language, cachedImageUrl } = publication
    await headerHelper.setHeaderModelClient(dispatch, { brand, model, modelType, year, language, cachedImageUrl })
  }, [dispatch, publication])

  useEffect(() => {
    if (!router.isReady) return
    if (storeSetupRanRef.current) return

    storeSetupRanRef.current = true

    initStore()
  }, [initStore, router.isReady])

  useEffect(() => {
    if (!router.isReady) return
    if (isServiceWarrantyMode) return
    if (!contentId) return
    if (pubRequestedRef.current) return

    pubRequestedRef.current = true

    fetchPublication(contentId)
  }, [router.isReady, contentId, fetchPublication, isServiceWarrantyMode])

  useEffect(() => {
    if (!router.isReady) return
    if (!isServiceWarrantyMode) return
    if (!apiHook.getBaseURL()) return

    const fetchServiceWarrantyContent = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await apiHook.get(`publications/service-warranty/content?${serviceWarrantyQueryString}`)
        const publicationBrand = data?.publication?.brand?.toLowerCase()

        if (publicationBrand) {
          dispatch(productActions.SET_BRAND(publicationBrand))
          dispatch(searchActions.SET_BRAND(publicationBrand))
        }

        setPublication(
          data?.publication
            ? { ...data.publication, translationUnderDevelopment: !!data.translationUnderDevelopment }
            : null
        )
        if (data?.contents) {
          setPublicationContentOverride({
            loading: false,
            data: { contents: data.contents, filesCache: [] },
            error: null
          })
        }
      } catch (err) {
        console.error('[content] service warranty content error:', err)
        setError('serviceWarrantyUnavailable')
      } finally {
        setLoading(false)
      }
    }

    fetchServiceWarrantyContent()
  }, [apiHook, dispatch, isServiceWarrantyMode, router.isReady, serviceWarrantyQueryString])

  // Cleanup when leaving the page
  useEffect(() => {
    return () => {
      dispatch(publicationActions.SET_RELATED_PUBLICATIONS({}))
    }
  }, [dispatch])

  useEffect(() => {
    if (publication?._id) {
      updateHeader()
    }
  }, [publication, updateHeader])

  if (loading) return <div>Loading...</div>
  if (error) {
    return isServiceWarrantyMode ? <ServiceWarrantyUnavailable /> : <div>Error: {error}</div>
  }
  if (!publication?._id) return <PublicationNotFound />

  const isPreviewMode =
    !isServiceWarrantyMode && router.query.publicationState === generalConstants.PUBLICATION_STATES.DRAFT

  return (
    <>
      {isPreviewMode && <PreviewModeBanner />}
      <BrandPublicationViewer
        brand={brand}
        topicId={topicId}
        publicationInfo={publication}
        publicationState={!isServiceWarrantyMode ? router.query.publicationState : undefined}
        publicationContentOverride={publicationContentOverride}
        showTocInitially={showTocInitially}
        withoutNav={withoutNavBool}
        skipToc={skipTocBool}
      />
    </>
  )
}

export const getServerSideProps = reduxWrapper.getServerSideProps(store =>
  withLogger(async ctx => {
    const { query } = ctx
    const { id, publicationType } = query

    await getInitialStoreSetup({ store, ctx })

    if (id) {
      try {
        const publicationFromSearchApi =
          (await apiWrapper.get(
            `publications`,
            {
              contentRef: id,
              ...(typeof publicationType === 'string' && publicationType ? { publicationType } : {})
            },
            ctx
          )) || {}

        if (publicationFromSearchApi?.items?.[0]) {
          const brand = publicationFromSearchApi.items[0]?.brand?.toLowerCase()
          if (brand) {
            store.dispatch(productActions.SET_BRAND(brand))
            store.dispatch(searchActions.SET_BRAND(brand))
          }
        }
      } catch (error) {
        logger.error('Error fetching publication in SSR:', error.message)
      }
    }

    return {
      props: {}
    }
  })
)

export default ContentPage
